import { Box, chakra, Flex, Grid, Skeleton, Text, VStack } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { DEMO_TONES, type DemoTone } from './demo-parts';
import { dayHeading, slotLabels } from './demo-slots';
import type { DemoBookingFlow } from './use-demo-booking-flow';

const SlotButton = ({
  slot,
  flow,
  tone,
  compact = false,
}: {
  slot: string;
  flow: DemoBookingFlow;
  tone: DemoTone;
  compact?: boolean;
}) => {
  const labels = slotLabels(slot, flow.locale, flow.timeZone);
  const isActive = flow.selected === slot;
  const styles = DEMO_TONES[tone];

  return (
    <chakra.button
      type="button"
      onClick={() => flow.pick(slot)}
      aria-pressed={isActive}
      aria-label={labels.full}
      h={compact ? '34px' : styles.slotHeight}
      px={compact ? 3 : 0}
      borderRadius="lg"
      borderWidth="1px"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap="1px"
      cursor="pointer"
      transition="background .15s, border-color .15s"
      css={isActive ? styles.slotActive : { ...styles.slotIdle, _hover: styles.slotHover }}
      _focusVisible={{ outline: '2px solid', outlineColor: 'lilac.400', outlineOffset: '1px' }}
    >
      {compact ? null : (
        <Text fontSize="11.5px" opacity={styles.dayOpacity}>
          {labels.day}
        </Text>
      )}
      <Text fontFamily="heading" fontSize={compact ? 'sm' : '15px'} fontWeight="semibold">
        {labels.time}
      </Text>
    </chakra.button>
  );
};

/**
 * The three slots put forward, then every slot of the coming business days
 * once the brand asks for others. Loading, empty and failed reads each say so
 * rather than leaving an empty grid.
 */
export const DemoSlotPicker = ({ flow, tone }: { flow: DemoBookingFlow; tone: DemoTone }) => {
  const { t } = useTranslation();
  const { muted, slotHeight } = DEMO_TONES[tone];

  if (flow.isLoadingSlots) {
    return (
      <Grid templateColumns="repeat(3, minmax(0, 1fr))" gap="6px" aria-busy="true">
        {[0, 1, 2].map((index) => (
          <Skeleton key={index} h={slotHeight} borderRadius="lg" />
        ))}
      </Grid>
    );
  }

  if (flow.slotsFailed || flow.hasNoSlots) {
    return (
      <Text fontSize="13px" color={muted}>
        {flow.slotsFailed
          ? t('demoInvitation.errors.slotsUnavailable')
          : t('demoInvitation.noSlots')}
      </Text>
    );
  }

  if (flow.showAll) {
    return (
      <VStack
        align="stretch"
        gap={3}
        maxH="220px"
        overflowY="auto"
        pr={1}
        aria-label={t('demoInvitation.allSlots')}
      >
        {flow.days
          .filter((day) => day.slots.length > 0)
          .map((day) => (
            <Box key={day.date}>
              <Text fontSize="12px" fontWeight="medium" color={muted} mb={1.5}>
                {dayHeading(day.date, flow.locale)}
              </Text>
              <Flex wrap="wrap" gap="6px">
                {day.slots.map((slot) => (
                  <SlotButton key={slot} slot={slot} flow={flow} tone={tone} compact />
                ))}
              </Flex>
            </Box>
          ))}
      </VStack>
    );
  }

  return (
    <Grid templateColumns="repeat(3, minmax(0, 1fr))" gap="6px">
      {flow.featured.map((slot) => (
        <SlotButton key={slot} slot={slot} flow={flow} tone={tone} />
      ))}
    </Grid>
  );
};
